import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import Buildvizer from "../../assets/images/Buildvizer.png";
import start_tower from "../../assets/images/stark_tower.jpg";
import { signupFreelancer } from "../../services/freelancer";
import { Form, Field, FormSubmit, AddressFields } from "../../components/form";
import { freelancerSignupSchema } from "../../services/login/schemas";

const DEFAULT_VALUES = {
  name: "",
  mobile: "",
  email: "",
  password: "",
  address: {
    addressId: 0,
    addressType: "CUSTOMER",
    buildingNo: "",
    streetName1: "",
    streetName2: "",
    city: "",
    district: "",
    pinCode: "",
    state: "",
    country: "India",
  },
};

function FreelancerSignup() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleSignup = async (data) => {
    try {
      await signupFreelancer(data);
      toast.success("Account created! Please login and set up your skills.");
      navigate("/login");
    } catch (error) {
      toast.error(
        error?.detail ||
          error?.response?.data?.detail ||
          "An error occurred during signup.",
      );
    }
  };

  return (
    <div
      className="flex justify-end items-center min-h-screen bg-slate-100 p-16"
      style={{ backgroundImage: `url(${start_tower})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="flex rounded-md bg-white shadow-lg max-w-2xl w-full overflow-hidden backdrop-blur-lg bg-opacity-90">
        <div className="w-full p-8 overflow-y-auto max-h-[90vh]">
          <button
            onClick={() => navigate("/login")}
            className="text-gray-600 hover:text-gray-800 mb-4 flex items-center gap-2 text-sm font-medium"
          >
            <ArrowLeft size={18} />
            Back to Login
          </button>

          <div className="flex justify-center mb-4">
            <img src={Buildvizer} alt="Company Logo" className="h-24 w-auto" />
          </div>

          <h2 className="text-2xl font-bold text-gray-800 text-center mb-1">Freelancer Signup</h2>
          <p className="text-center text-sm text-gray-500 mb-6">
            Grow your architecture career with paid projects
          </p>

          <Form
            schema={freelancerSignupSchema}
            defaultValues={DEFAULT_VALUES}
            onSubmit={handleSignup}
            className="space-y-5"
          >
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Your Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field name="name" label="Full Name" placeholder="Enter your full name" required />
                <Field name="mobile" label="Mobile Number" type="phone" placeholder="Mobile number" required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field name="email" label="Email" type="email" placeholder="Enter your email" required />
                <Field
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  required
                  endAdornment={
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="text-gray-500 hover:text-gray-700 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  }
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Address</h3>
              <AddressFields prefix="address" />
            </div>

            <FormSubmit loadingLabel="Creating account..." block>
              Create Account
            </FormSubmit>

            <p className="text-center text-sm text-gray-600 mt-4">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-semibold text-flamingo-600 hover:text-flamingo-700"
              >
                Sign in
              </button>
            </p>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default FreelancerSignup;
